/**
 * The main window's tab strip and its iframe panels.
 *
 * Signed in, at desktop width, the main window shows a slim browser-like strip
 * at the top of the content area (right of the sidebar / rail, above the page)
 * with the page you are on as tab 1 and a "+" to open another page as a tab —
 * so a tabbed experience never needs a second browser tab or window. State and
 * behaviour live in useMainWindowTabs; this file only draws them (the strip
 * itself — reorder, pin, move between windows — is the shared TabStrip).
 *
 *   MainTabStrip   the strip itself: tabs, per-tab menu, "+"
 *   MainTabPanels  the iframe tabs, placed where the page content goes
 */
import { useTranslation } from 'react-i18next';
import { AppWindow, Copy, Plus } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { appTabKey, type AppTabSpec } from '@/lib/popout';
import { PagePickerItems, type PageOption } from './PagePickerMenu';
import { TabStrip, type StripTab } from './TabStrip';
import { frameRef } from './useFrameBridge';
import { PRIMARY_UID, type MainTab, type MainWindowTabsApi } from './useMainWindowTabs';

/** Strip height. Iframe tabs fill the rest of the content area below it. */
export const MAIN_TAB_STRIP_PX = 36;

interface StripProps {
  api: MainWindowTabsApi;
  pages: PageOption[];
  labelFor: (spec: AppTabSpec) => string;
  isDesktop: boolean;
}

export function MainTabStrip({ api, pages, labelFor, isDesktop }: StripProps) {
  const { t } = useTranslation();
  const isMac = isDesktop || (typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform));

  const labelOf = (tab: MainTab) => tab.title || labelFor(api.specOf(tab));
  const openKeys = new Set(api.tabs.map((x) => appTabKey(api.specOf(x))));
  const activeTab = api.tabs.find((x) => x.uid === api.activeUid) ?? api.tabs[0];

  const stripTabs: StripTab[] = api.tabs.map((tab) => {
    const isPrimary = tab.uid === PRIMARY_UID;
    return {
      uid: tab.uid,
      key: appTabKey(api.specOf(tab)),
      label: labelOf(tab),
      pinned: tab.pinned,
      home: isPrimary,
      // The home tab is the app itself (the live native page): it can be
      // reordered and pinned, but never closed or moved to another window.
      closable: !isPrimary,
      movable: !isPrimary,
      domId: `main-tab-${tab.uid}`,
      panelId: isPrimary ? 'main-content' : `main-tab-panel-${tab.uid}`,
      testId: isPrimary ? 'main-tab-primary' : `main-tab-${tab.uid}`,
      closeTestId: `main-tab-close-${tab.uid}`,
      menuTestId: `main-tab-menu-${tab.uid}`,
    };
  });

  return (
    <TabStrip
      tabs={stripTabs}
      activeUid={api.activeUid}
      ariaLabel={t('appPopout.tabs_label', 'Open pages')}
      testIdPrefix="main-tab"
      isDesktop={isDesktop}
      variant="main"
      className="flex flex-shrink-0 items-end gap-1 border-b border-slate-200 bg-slate-100/80 px-2 pt-1 dark:border-slate-800 dark:bg-slate-900/80"
      style={{ height: MAIN_TAB_STRIP_PX }}
      testId="main-tab-strip"
      onActivate={api.activate}
      onClose={api.closeTab}
      onMoveTo={api.moveTabTo}
      onPin={api.setPinned}
      onCloseOthers={api.closeOthers}
      onMoveToNewWindow={(uid) => void api.moveToWindow(uid)}
      onMoveToTarget={(uid, target) => void api.moveToWindow(uid, target)}
      listTargets={api.move.listTargets}
      startDrag={api.startDrag}
      onDropForeign={api.dropForeign}
      onDragOut={api.dragOut}
      menuExtras={(tab) => (
        <>
          {tab.home && (
            <DropdownMenuItem
              className="gap-2"
              onSelect={() => void api.moveToWindow(tab.uid)}
              data-testid="main-tab-move-window"
            >
              <AppWindow className="h-4 w-4" />
              {t('mainTabs.open_in_window', 'Open in new window')}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem className="gap-2" onSelect={() => api.duplicate(tab.uid)} data-testid="main-tab-duplicate">
            <Copy className="h-4 w-4" />
            {t('mainTabs.duplicate', 'Duplicate')}
          </DropdownMenuItem>
        </>
      )}
      addControl={
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="mb-[3px] inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-slate-600 hover:bg-slate-200 dark:text-slate-300 dark:hover:bg-slate-800"
              aria-label={t('mainTabs.new_tab_button', 'New tab')}
              title={isDesktop ? `${t('mainTabs.new_tab_button', 'New tab')} (${isMac ? '⌘T' : 'Ctrl+T'})` : t('mainTabs.new_tab_button', 'New tab')}
              data-testid="main-tab-add"
            >
              <Plus className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="max-h-[70vh] w-64 overflow-y-auto">
            <DropdownMenuItem
              className="gap-2"
              onSelect={() => {
                if (activeTab) api.addTab(api.specOf(activeTab));
              }}
              data-testid="main-tab-new"
            >
              <Plus className="h-4 w-4" />
              {t('mainTabs.new_tab', 'New tab with this page')}
              {isDesktop && <DropdownMenuShortcut>{isMac ? '⌘T' : 'Ctrl+T'}</DropdownMenuShortcut>}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground">{t('mainTabs.open_page', 'Open a page')}</DropdownMenuLabel>
            <PagePickerItems
              pages={pages}
              openKeys={openKeys}
              testIdPrefix="main-tab-add-"
              onPick={(p) => api.addTab({ tab: p.tab })}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      }
    />
  );
}

interface PanelsProps {
  api: MainWindowTabsApi;
  labelFor: (spec: AppTabSpec) => string;
}

/** The iframe tabs. Each mounts on first view, then stays mounted (hidden) so its state survives. */
/**
 * While an iframe tab is in front the shell's <main> is a fixed-height flex
 * column (strip + this panel), so the active panel just takes the rest of it —
 * no viewport arithmetic that drifts whenever <main> gains padding or chrome.
 */
export function MainTabPanels({ api, labelFor }: PanelsProps) {
  return (
    <>
      {api.tabs.map((tab) => {
        if (!tab.spec) return null;
        const isActive = tab.uid === api.activeUid;
        return (
          <div
            key={tab.uid}
            role="tabpanel"
            id={`main-tab-panel-${tab.uid}`}
            aria-labelledby={`main-tab-${tab.uid}`}
            data-testid={`main-tab-panel-${tab.uid}`}
            className={isActive ? 'flex min-h-0 flex-1 flex-col overflow-hidden' : 'hidden'}
          >
            {tab.mounted && tab.src && (
              <iframe
                ref={frameRef(api.frames, tab.uid)}
                src={tab.src}
                // Same-origin page frames may run an in-app call (TRUS-668).
                allow="camera; microphone; autoplay; display-capture"
                title={tab.title || labelFor(tab.spec)}
                className="h-full min-h-0 w-full flex-1 border-0"
                data-testid={`main-tab-frame-${tab.uid}`}
              />
            )}
          </div>
        );
      })}
    </>
  );
}
